import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Lock, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

function formatErr(detail) {
  if (!detail) return 'Failed. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function ChangePassword() {
  const { user, checkAuth } = useContext(AuthContext);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const isForced = !!user?.must_change_password;

  // Password strength signal
  const strength = (() => {
    let s = 0;
    if (newPwd.length >= 8) s++;
    if (/[A-Z]/.test(newPwd)) s++;
    if (/[a-z]/.test(newPwd)) s++;
    if (/[0-9]/.test(newPwd)) s++;
    if (/[^A-Za-z0-9]/.test(newPwd)) s++;
    return s;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPwd.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPwd !== confirmPwd) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const body = { new_password: newPwd };
      if (!isForced) body.current_password = currentPwd;
      await axios.post(`${API}/auth/change-password`, body, { withCredentials: true });
      toast.success('Password changed successfully');
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white flex flex-col">
      <header className="backdrop-blur-xl bg-[#0a0f1e]/70 border-b border-teal-900/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo variant="icon" size={40} className="rounded-xl ring-1 ring-teal-500/30" />
            <div className="leading-tight">
              <div className="text-lg font-bold">FM Flexi Pro<span className="text-teal-400 text-xs align-top ml-0.5">™</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">by SKG Apps</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">Signed in as <span className="text-teal-300">{user?.email}</span></div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex p-3 rounded-2xl bg-teal-500/10 border border-teal-500/30 mb-4">
              <Shield className="w-6 h-6 text-teal-400" />
            </div>
            <h1 className="text-3xl font-bold">{isForced ? 'Set your new password' : 'Change password'}</h1>
            <p className="text-sm text-slate-400 mt-2">
              {isForced
                ? 'Your account uses a temporary password. Please set a permanent password to continue.'
                : 'Update your password to keep your account secure.'}
            </p>
          </div>

          <Card className="bg-slate-900/60 border-teal-900/40 backdrop-blur p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
              {!isForced && (
                <div>
                  <Label className="text-slate-200">Current password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      data-testid="current-password-input"
                      type="password"
                      autoComplete="current-password"
                      value={currentPwd}
                      onChange={(e) => setCurrentPwd(e.target.value)}
                      className="pl-10 bg-slate-950 border-slate-700 text-white"
                      required
                    />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-slate-200">New password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="new-password-input"
                    type="password"
                    autoComplete="new-password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    className="pl-10 bg-slate-950 border-slate-700 text-white"
                    required
                    minLength={8}
                  />
                </div>
                {newPwd && (
                  <div className="mt-2 flex gap-1">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded ${i <= strength ? (strength <= 2 ? 'bg-red-500' : strength <= 3 ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-slate-700'}`} />
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-1.5">Min 8 characters. Use upper + lower + numbers for best strength.</p>
              </div>
              <div>
                <Label className="text-slate-200">Confirm new password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="confirm-password-input"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    className="pl-10 bg-slate-950 border-slate-700 text-white"
                    required
                  />
                </div>
                {confirmPwd && newPwd === confirmPwd && (
                  <p className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Passwords match</p>
                )}
              </div>

              {error && (
                <div data-testid="change-pw-error" className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                data-testid="submit-change-password"
                type="submit"
                disabled={submitting || !newPwd || !confirmPwd}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold"
              >
                {submitting ? 'Saving...' : 'Save new password'}
              </Button>
            </form>
          </Card>
        </div>
      </main>
    </div>
  );
}
